import { useEffect, useRef, useState } from 'react';
import { api, type LatePayRow } from '../lib/api';
import { useMe } from '../lib/me';
import Modal from './Modal';

/**
 * 마감 후 지급 대기(2026-11-05 way · 서버 `payGateFreeze.ts`) — 영업 기한이 지난 달은 기사 명세 미수 게이트가 굳는다.
 * 굳힌 뒤 청구가 걸려 새로 열린 접수는 「대기」 — 필요한 팀(관리기사A = 회계팀·인차팀 · 그 밖 = 회계팀)이 다 「승인」해야(관리자는 한 번에) 그 달 명세에 들어간다.
 * 그 달 기사 마감 뒤엔 반영 불가(서버가 막음 · 「마감 후 수정」 기안 안내).
 *
 *   LatePayButton = 정산 › 마감 띠(CloseDeadlineRow) 옆 버튼(그 달 대기 수)
 *   LatePayPanel  = 목록 창(mon 없으면 모든 달 대기만)
 *   LatePayAlert  = 인차팀·회계팀 팝업(10분마다 · 「확인」은 이 브라우저 · 새 대기가 생기거나 다음 날이면 다시)
 */

const won = (s: string) => Math.round(Number(s)).toLocaleString('ko-KR');
const TEAM_SCREEN: Record<string, 'result-mo' | 'accounting'> = { 인차팀: 'result-mo', 회계팀: 'accounting' };
type MeLite = { role?: string; team?: string | null; screens?: Partial<Record<string, string>> } | null | undefined;
/** 아직 승인 안 한 팀(서버 `pendingTeams` 와 같은 판정). */
const pendingOf = (r: LatePayRow) => r.teams.filter((t) => (t === '회계팀' ? !r.accOk : !r.hrOk));
/** 이 사람이 찍을 승인(서버 `approvalsFor` 사본 — 버튼 보이기용 · 판정은 서버). */
const myApprovals = (me: MeLite, r: LatePayRow) =>
  !me ? [] : me.role === 'admin' ? pendingOf(r)
    : pendingOf(r).filter((t) => { const s = TEAM_SCREEN[t]; return me.team === t && !!s && me.screens?.[s] === 'write'; });
const monLabel = (m: string) => `${m.slice(0, 4)}년 ${Number(m.slice(5, 7))}월`;

export function LatePayButton({ driveMon }: { driveMon: string }) {
  const [rows, setRows] = useState<LatePayRow[] | null>(null);
  const [frozenAt, setFrozenAt] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [nonce, setNonce] = useState(0);
  useEffect(() => {
    if (!driveMon) return;
    let alive = true;
    api.listLatePay({ mon: driveMon })
      .then((r) => { if (alive) { setRows(r.rows); setFrozenAt(r.frozenAt); } })
      .catch(() => { if (alive) setRows(null); }); // 권한 없음 = 버튼 숨김
    return () => { alive = false; };
  }, [driveMon, nonce]);
  if (!rows || (!frozenAt && rows.length === 0)) return null;
  const waits = rows.filter((r) => r.state === 'wait').length;
  return (
    <>
      <button
        className={`rounded px-1.5 py-0.5 text-xs ${waits ? 'bg-red-100 font-bold text-red-700' : 'bg-slate-100 text-ink-soft'}`}
        title={frozenAt ? `${frozenAt} 에 지급 대상을 굳혔습니다 — 그 뒤 청구된 접수는 필요한 팀 「승인」 전까지 명세에 안 들어갑니다.` : undefined}
        onClick={() => setOpen(true)}
      >
        마감 후 지급 대기 {waits ? `${waits}건` : '0'}
      </button>
      {open && <LatePayPanel mon={driveMon} onClose={() => { setOpen(false); setNonce((n) => n + 1); }} />}
    </>
  );
}

export function LatePayPanel({ mon, onClose }: { mon: string | null; onClose: () => void }) {
  const { me } = useMe();
  const [data, setData] = useState<{ frozenAt: string | null; rows: LatePayRow[] } | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);
  useEffect(() => {
    let alive = true;
    api.listLatePay(mon ? { mon } : {}).then((r) => { if (alive) setData(r); }).catch((e) => { if (alive) setErr(e instanceof Error ? e.message : String(e)); });
    return () => { alive = false; };
  }, [mon, nonce]);

  async function release(id: string) {
    setBusy(id);
    setErr(null);
    try {
      await api.releaseLatePay({ id });
      setConfirm(null);
      setNonce((n) => n + 1);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  }

  return (
    <Modal width="max-w-4xl">
      <div className="card space-y-3 p-5">
        <div className="flex items-center gap-2">
          <div className="text-lg font-bold">마감 후 지급 대기{mon ? ` — ${monLabel(mon)}` : ''}</div>
          <button className="btn-ghost ml-auto" onClick={onClose}>닫기</button>
        </div>
        <p className="text-sm text-ink-soft">
          영업 마감 기한이 지난 달은 그때 지급 조건이 열려 있던 접수로 명세가 굳습니다. 그 뒤 청구가 걸린 접수는 여기 「대기」로 남고,
          필요한 팀이 모두 「승인」해야 그 달 명세에 들어갑니다 — 관리기사A가 있는 접수는 회계팀·인차팀, 그 밖은 회계팀(굳힌 뒤 청구가 지워진 접수는 그대로 지급).
          {data?.frozenAt && <> 굳힌 시각 {data.frozenAt}.</>}
        </p>
        {err && <div className="rounded bg-red-50 px-2 py-1 text-sm text-red-700">{err}</div>}
        {!data && !err && <div className="text-sm text-ink-faint">불러오는 중…</div>}
        {data && data.rows.length === 0 && <div className="text-sm text-ink-faint">대기 없음</div>}
        {data && data.rows.length > 0 && (
          <div className="max-h-[60vh] overflow-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-ink-soft">
                  <th className="px-2 py-1">정산월</th>
                  <th className="px-2 py-1">접수</th>
                  <th className="px-2 py-1">거래처</th>
                  <th className="px-2 py-1">영업정산(기사별)</th>
                  <th className="px-2 py-1 text-right">노선 운행료</th>
                  <th className="px-2 py-1">찾은 때</th>
                  <th className="px-2 py-1">승인</th>
                  <th className="px-2 py-1">상태</th>
                </tr>
              </thead>
              <tbody>
                {data.rows.map((r) => (
                  <tr key={r.id} className="border-t align-top">
                    <td className="px-2 py-1">{r.driveMon}</td>
                    <td className="px-2 py-1">{r.recvId} <span className="text-xs text-ink-faint">{r.recvType}</span></td>
                    <td className="px-2 py-1">{r.custName ?? '—'}</td>
                    <td className="px-2 py-1 text-xs">
                      {r.drivers.length === 0 ? <span className="text-ink-faint">—</span> : r.drivers.map((d) => (
                        <div key={String(d.driverId)}>
                          {d.driverName ?? `기사 ${d.driverId}`}
                          {Number(d.plus) !== 0 && <span className="ml-1 text-green-700">+{won(d.plus)}</span>}
                          {Number(d.minus) !== 0 && <span className="ml-1 text-red-600">-{won(d.minus)}</span>}
                        </div>
                      ))}
                    </td>
                    <td className="px-2 py-1 text-right">{Number(r.routeFee) !== 0 ? won(r.routeFee) : '—'}</td>
                    <td className="px-2 py-1 text-xs">{r.detectedAt}</td>
                    <td className="px-2 py-1 text-xs whitespace-nowrap">
                      {r.teams.map((t) => {
                        const okv = t === '회계팀' ? r.accOk : r.hrOk;
                        return (
                          <div key={t} title={okv ? `${okv.at ?? ''} · ${okv.name ?? ''}` : undefined}>
                            {t} {okv ? <span className="text-green-700">✓ {okv.name}</span> : <span className="text-red-600">대기</span>}
                          </div>
                        );
                      })}
                    </td>
                    <td className="px-2 py-1 text-xs">
                      {r.state === 'released' ? (
                        <span className="text-ink-soft">반영 {r.releasedAt} · {r.releasedBy}</span>
                      ) : r.driverClosed ? (
                        <span className="text-red-600" title="기사 마감된 달이라 반영할 수 없습니다 — 「마감 후 수정」 기안으로 처리하세요.">대기 · 기사 마감됨</span>
                      ) : myApprovals(me, r).length === 0 ? (
                        <span className="font-bold text-red-700">대기</span>
                      ) : confirm === r.id ? (
                        <span className="flex gap-1">
                          <button className="btn-primary px-2 py-0.5 text-xs" disabled={busy === r.id} onClick={() => release(r.id)}>{busy === r.id ? '…' : '정말 승인'}</button>
                          <button className="btn-ghost px-2 py-0.5 text-xs" onClick={() => setConfirm(null)}>취소</button>
                        </span>
                      ) : (
                        <button className="btn-primary px-2 py-0.5 text-xs" onClick={() => setConfirm(r.id)}>승인({myApprovals(me, r).join('·')})</button>
                      )}
                      {r.state === 'wait' && !r.hasAmount && <div className="text-ink-faint">명세 금액 없음</div>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Modal>
  );
}

const ACK_KEY = 'late-pay-ack-v1';
const FETCH_MS = 10 * 60_000;
const kstDay = () => new Date(Date.now() + 9 * 3600_000).toISOString().slice(0, 10);
function readAck(): { day: string; ids: string[] } {
  try { return JSON.parse(localStorage.getItem(ACK_KEY) ?? '') as { day: string; ids: string[] }; } catch { return { day: '', ids: [] }; }
}

/** 팝업 — 인차팀(월정기 입력)·회계팀(회계 입력)(관리자 제외 · 서버 DM 받는 사람과 같은 판정). 내 팀 승인이 남은 금액 있는 대기만 센다. */
export function LatePayAlert() {
  const { me } = useMe();
  const [pop, setPop] = useState<LatePayRow[] | null>(null);
  const [panel, setPanel] = useState(false);
  const last = useRef<LatePayRow[]>([]);
  useEffect(() => {
    // 11-05 way — 관리기사A = 인차팀·회계팀 · 관리기사B = 회계팀(마감 후 수정과 같은 승인) · 팝업도 그 팀만
    const scr = me?.team ? TEAM_SCREEN[me.team] : undefined;
    if (!me || me.role === 'admin' || !scr || me.screens?.[scr] !== 'write') return;
    let alive = true;
    const load = () => {
      api.listLatePay({}).then((r) => {
        if (!alive) return;
        const waits = r.rows.filter((x) => x.state === 'wait' && x.hasAmount && !x.driverClosed && myApprovals(me, x).length > 0);
        last.current = waits;
        const ack = readAck();
        const fresh = ack.day !== kstDay() || waits.some((w) => !ack.ids.includes(w.id));
        setPop(waits.length && fresh ? waits : null);
      }).catch(() => { /* 알림은 없어도 업무 화면은 그대로 */ });
    };
    load();
    const t = setInterval(load, FETCH_MS);
    return () => { alive = false; clearInterval(t); };
  }, [me]);

  const ack = () => {
    try { localStorage.setItem(ACK_KEY, JSON.stringify({ day: kstDay(), ids: last.current.map((w) => w.id) })); } catch { /* 다음 박자에 다시 뜬다 */ }
    setPop(null);
  };
  if (panel) return <LatePayPanel mon={null} onClose={() => { setPanel(false); ack(); }} />;
  if (!pop) return null;
  const mons = [...new Set(pop.map((p) => p.driveMon))].sort().map(monLabel).join('·');
  return (
    <Modal width="max-w-lg">
      <div className="card space-y-3 p-5">
        <div className="text-lg font-bold">💰 마감 후 지급 대기 {pop.length}건</div>
        <p className="text-sm text-ink-soft">
          {mons} 영업 마감 기한이 지난 뒤 청구가 걸려 지급 조건이 열린 접수가 있습니다. {me?.team} 「승인」이 남아 있고, 필요한 팀이 모두 승인하기 전까지는 그 달 명세에 들어가지 않습니다.
        </p>
        <ul className="text-sm">
          {pop.slice(0, 6).map((p) => (
            <li key={p.id}>· {p.driveMon} 접수 {p.recvId} {p.custName ?? ''} — 기사 {p.drivers.length}명{Number(p.routeFee) !== 0 ? ` · 노선 운행료 ${won(p.routeFee)}원` : ''}</li>
          ))}
          {pop.length > 6 && <li className="text-ink-faint">외 {pop.length - 6}건</li>}
        </ul>
        <div className="flex justify-end gap-2">
          <button className="btn-ghost" onClick={ack}>확인</button>
          <button className="btn-primary" onClick={() => setPanel(true)}>지금 보기</button>
        </div>
      </div>
    </Modal>
  );
}
